import type { Country } from '../../constants/country/country';
import { CountryCurrency, CURRENCY_INFO, Currency } from '../../constants/currency/currency';
import { SEPTEMBER_CREDIT_AMOUNTS, SEPTEMBER_CREDIT_EXPIRES_AT } from '../../constants/campaign/septemberCredit';

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;
const CLOCK_REFRESH_MS = 60_000;

/** Align updates to the next day-count boundary or expiry, even if mounted between minute ticks. */
export const getSeptemberCreditRefreshDelay = (now: Date): number | null => {
  const remainingMs = Date.parse(SEPTEMBER_CREDIT_EXPIRES_AT) - now.getTime();
  if (!Number.isFinite(remainingMs) || remainingMs <= 0) {
    return null;
  }
  return Math.min(CLOCK_REFRESH_MS, remainingMs % MILLISECONDS_PER_DAY || MILLISECONDS_PER_DAY);
};

interface SeptemberCreditOfferParams {
  country: string | undefined;
  isAuthenticated: boolean;
  now: Date;
}

export interface SeptemberCreditOffer {
  amountLabel: string;
  headline: string;
  description: string;
  expiryText: string;
  daysRemaining: number;
}

export const getSeptemberCreditOffer = ({
  country,
  isAuthenticated,
  now,
}: SeptemberCreditOfferParams): SeptemberCreditOffer | null => {
  const remainingMs = Date.parse(SEPTEMBER_CREDIT_EXPIRES_AT) - now.getTime();
  if (
    !Number.isFinite(remainingMs) ||
    remainingMs <= 0 ||
    !country ||
    !Object.hasOwn(SEPTEMBER_CREDIT_AMOUNTS, country)
  ) {
    return null;
  }

  const supportedCountry = country as Country;
  const currency = CountryCurrency[supportedCountry];
  const { symbol, position } = CURRENCY_INFO[currency];
  const amount = SEPTEMBER_CREDIT_AMOUNTS[supportedCountry].toLocaleString('en-US', {
    useGrouping: currency !== Currency.Colombia,
  });
  const amountLabel =
    position === 'suffix' ? `${amount} ${symbol}` : `${symbol}${currency === Currency.Switzerland ? ' ' : ''}${amount}`;
  const daysRemaining = Math.ceil(remainingMs / MILLISECONDS_PER_DAY);

  return {
    amountLabel,
    headline: isAuthenticated
      ? `Use ${amountLabel} in Credits on Your Order`
      : `Sign Up Now and Get ${amountLabel} in Credits`,
    description: isAuthenticated ? 'It’s already in your account.' : 'It’s added to your account right away.',
    expiryText: isAuthenticated
      ? `It expires in ${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'}.`
      : 'Use it by Sep 30.',
    daysRemaining,
  };
};
